import { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("bvs_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = localStorage.getItem("bvs_token");
    if (t && !user) {
      setLoading(true);
      api.get("/auth/me")
        .then((r) => {
          setUser(r.data);
          localStorage.setItem("bvs_user", JSON.stringify(r.data));
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
    // Sliding session: silently swap in a fresh 30-day token on every app
    // open (and every 12h while the tab stays up) so this browser never
    // logs out as long as it's used occasionally.
    const refresh = () => {
      if (!localStorage.getItem("bvs_token")) return;
      api.post("/auth/refresh")
        .then((r) => { if (r.data?.token) localStorage.setItem("bvs_token", r.data.token); })
        .catch(() => {});
    };
    if (t) refresh();
    const iv = setInterval(refresh, 12 * 60 * 60 * 1000);
    return () => clearInterval(iv);
  }, []); // eslint-disable-line

  const login = async (email, password) => {
    const normalizedEmail = (email || "").trim().toLowerCase();
    const { data } = await api.post("/auth/login", { email: normalizedEmail, password });
    localStorage.setItem("bvs_token", data.token);
    localStorage.setItem("bvs_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("bvs_token", data.token);
    localStorage.setItem("bvs_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    // Best-effort call the backend logout so the HttpOnly session cookie
    // is cleared (iteration 156 regression — the local-only clear left
    // /api/auth/me returning 200 for the previous user).
    try {
      await api.post("/auth/logout");
    } catch (_) { /* ignore — always clear local state */ }
    localStorage.removeItem("bvs_token");
    localStorage.removeItem("bvs_user");
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
